import * as _ from 'lodash';

import { Component, Injector, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PaymentConfig } from 'src/app/app.interfaces';
import { CreditCard } from 'src/app/shared/classes/credit-card/credit-card.class';
import { PaymentsConfig } from 'src/app/shared/classes/projects/payments/payments-config.class';
import { Screen } from 'src/app/shared/models/screen/screen.model';
import { CreditCardFormService } from 'src/app/shared/modules/components/credit-card-form/credit-card-form.service';
import { CreditCardErrorsMessages, CreditCardErrorsTitle } from 'src/app/shared/modules/components/credit-card-form/enum/errors.enum';
import { FeedbackModal } from 'src/app/shared/modules/components/modals/feedback-modal/feedback-modal.class';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { InjectorService } from 'src/app/shared/services/injector/injector.service';
import { TokenizeTemplate } from './tokenize-template.class';

@Component({
  selector: 'app-tokenize',
  templateUrl: './tokenize.component.html',
  styleUrls: ['./tokenize.component.scss']
})
export class TokenizeComponent implements OnDestroy {
  [x: string]: any;
  public config: PaymentsConfig['config'];
  public configService: ConfigService;
  public cta: { [key: string]: { [key: string]: any } };
  public errors: { form: boolean; };
  public feedback: { [key: string]: FeedbackModal };
  public loading: boolean;
  public isXS: boolean;
  public screenWidth: number;
  public template: TokenizeTemplate;
  public numberOfMethods = 0;
  private _creditCard: CreditCard;
  private _creditCardFormService: CreditCardFormService;
  private _form: FormGroup;
  public paymentConfig: PaymentConfig;
  public subscribers: Subscription[];

  private companyPhone: string;

  constructor() {
    const injector: Injector = InjectorService.get();

    Object.assign(this, {
      _creditCard: injector.get(ConfigService).booking.creditCard,
      _creditCardFormService: injector.get(CreditCardFormService),
      feedback: {
        error: new FeedbackModal({
          cta: { slug: 'understood', params: {}, action: () => this.feedback.error.close() },
          description: { slug: 'tokenize-credit-card-error-description', params: {} },
          icon: 'avc-cross-circle',
          title: { slug: 'tokenize-credit-card-error-title', params: {} }
        }),
        success: new FeedbackModal({
          cta: { slug: 'understood', params: {}, action: () => this.feedback.success.close() },
          description: { slug: 'tokenize-credit-card-success-description', params: {} },
          icon: 'avc-check-circle',
          title: { slug: 'tokenize-credit-card-success-title', params: {} }
        }),
        loading: new FeedbackModal({
          description: { slug: 'loading-purchase-description', params: {} },
          loader: true,
          title: { slug: '', params: {} }
        })
      },
      template: new TokenizeTemplate(),
      companyPhone: injector.get(ConfigService).company.phone,
      paymentConfig: injector.get(ConfigService).booking.resume.paymentConfig,
      configService: injector.get(ConfigService)
    });

    this.setConfig();
    this.setCta();
    this.setSubscribers();

    this.numberOfMethods = (this.config) ? this.config.waysOfPay.numberOfMethods : 0;

    if (this.paymentConfig.gatewayCode === 'mercadopago') {
      this.loadMercadoPagoScript();
    }
  }

  private setConfig(): void {
    const { project, ...config } = InjectorService.get().get(ConfigService);

    this.config = _.get(config[project], 'config.config');
  }

  public ngOnDestroy(): void {
    if (this.subscribers) {
      this.subscribers.forEach((s) => s.unsubscribe());
    }
  }

  private setSubscribers(): void {
    const screen: Screen = InjectorService.get().get(ConfigService).screen;

    const subscribers: Subscription[] = [
      screen.onIsXSChanges().subscribe((r) => {
        this.isXS = r;
      }),
      screen.onWindowResizeChanges().subscribe(() => this.screenWidth = screen.width)
    ];
    Object.assign(this, {
      isXS: screen.isXS,
      screenWidth: screen.width,
      subscribers
    });
  }

  public setCta(): void {
    this.cta = {
      next: {
        handler: () => this.onCta(),
        text: 'add'
      },
      back: {
        hide: true
      }
    };
  }

  public onFormChanges(params: { form: FormGroup; template: string; }): void {
    this._form = params.form;
  }

  public get form(): FormGroup {
    return this._form;
  }

  public get creditCardFormService(): CreditCardFormService {
    return this._creditCardFormService;
  }

  public onCta(): void {
    if (this._form.invalid) {
      this.errors = {
        ...this.errors,
        form: true
      };

      return;
    }

    this._tokenize(false);
  }

  public tokenize(showLoading = false): void {
    this._tokenize(showLoading);
  }

  private _setBillingAddress(billingAddress: any): void {
    this.creditCardFormService.updateBillingAddress({ billingAddress }).subscribe(
      (res) => this.onSuccess('tokenize', res),
      (error) => this.onError('tokenize', error)
    );
  }

  private _tokenize(showLoading): void {
    const value = this._form.getRawValue();
    const billingAddress = {
      ...(value.address) && { street: value.address },
      ...(value.city) && { city: (typeof value.city === 'object') ? value.city.value : value.city },
      ...(value.region) && { province: (typeof value.region === 'object') ? value.region.value : value.region },
      ...(value.postalCode) && { postalCode: value.postalCode },
      ...(value.phoneNumber) && { phoneNumber: value.phoneNumber },
      ...(value.country) && { country: value.country },
      ...(value.firstname) && { firstname: value.firstname },
      ...(value.lastname) && { lastname: value.lastname },
      ...(value.email) && { email: value.email }
    };

    this.loading = true;
    if (showLoading) {
      this.feedback.loading.open();
    }

    this.creditCardFormService.tokenize({
      ...(Object.keys(billingAddress).length > 0) && { billingAddress },
      creditCard: {
        expiryDate: `0${value.month}${`${value.year}`.slice(-2)}`.slice(-4),
        name: value.name,
        number: value.pan.replace(/\s+/g, ''),
        cvc: value.cvc
      }
    }).subscribe(
      (res: {
        billingAddress: { [key: string]: any; };
        canChange: boolean;
        display: string;
        holder: string;
        scheme: string;
        expMonth: string;
        expYear: string;
      }) => this.onSuccess('tokenize', res),
      (error) => this.onError('tokenize', error)
    );
  }

  private _handleCheckoutRedirect() {
    const { sourceApp, selectedPayment } = this.config;
    const { booking, return_url } = this.configService;
    const { id: creditCardId } = this._creditCard;

    if (sourceApp === 'olb') {
      const hashedBookingId = btoa(String(booking.id));
      const hashedTokenId = btoa(String(creditCardId));
      const deviceId = (window as any).MP_DEVICE_SESSION_ID;

      let url = `${return_url}reservarAlojamiento.php?booking_id=${hashedBookingId}&token_id=${hashedTokenId}`;

      if (selectedPayment) {
        const hashedPaymentId = btoa(String(selectedPayment.id));
        url += `&payment_id=${hashedPaymentId}`;
      }

      if (deviceId) {
        const hashedDeviceId = btoa(deviceId);
        url += `&deviceId=${hashedDeviceId}`;
      }

      window.location.href = url;
    }

    if (sourceApp === 'checkin') {
      window.location.href = `${return_url}`;
    }

    if (sourceApp === 'intranet') {
      window.location.href = `${return_url}`;
    }
  }

  public onSuccess(operation: 'purchase' | 'tokenize', value?: any): void {
    this._onSuccess(operation, value);

    if (this.configService.project === 'checkout') {
      this._handleCheckoutRedirect();
    }
  }

  private _onSuccess(operation: 'purchase' | 'tokenize', value?: any): void {
    this.loading = false;
    this.feedback.loading.close();
    this.feedback.success.open();

    if (value.display) {
      this.creditCard.set({
        updatable: value.canChange,
        number: value.display,
        type: value.scheme,
        owner: {
          name: value.holder,
          ...(value.billingAddress || {})
        }
      });

      return;
    }

    this.creditCard.setBillingAddress(value);
  }

  public onError(operation: 'purchase' | 'tokenize', error: any): void {
    this._onError(operation, error);
  }

  private _onError(operation: 'purchase' | 'tokenize', error: any): void {
    this.loading = false;
    this.feedback.error.set({
      description: {
        slug: CreditCardErrorsMessages[error] || 'error-try-contact',
        params: {
          amount: (operation === 'purchase') ? this.template.getAmountTranslation() : 0,
          phone: this.companyPhone
        }
      },
      title: {
        slug: CreditCardErrorsTitle[error] || 'error-processing-transaction',
        params: {}
      }
    });

    this.feedback.loading.close();
    this.feedback.error.open();
  }

  public get creditCard(): CreditCard {
    return this._creditCard;
  }

  public backHome() {
    this.configService[this.configService.project].goHome();
  }

  private loadMercadoPagoScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const node = document.createElement('script');
      node.src = 'https://www.mercadopago.com/v2/security.js';
      node.type = 'text/javascript';
      node.async = false;
      node.setAttribute('view', 'checkout');
      node.onload = () => resolve();
      node.onerror = () => reject(console.error('Failed on MP device'));
      document.getElementsByTagName('head')[0].appendChild(node);
    });
  }
}
